import { z } from '@customerio/hydra-validation';
import type { Condition, QuerySpec, SpecValue } from './spec-form';

// The server validates the full spec; the client parses the v1 shape the form
// reads and keeps unknown keys at every level, so nothing the agent sent is
// dropped.
const scalarSchema = z.union([z.string(), z.number(), z.boolean()]);
const specValueSchema: z.ZodType<SpecValue> = z.union([
  scalarSchema,
  z.array(scalarSchema),
  z.object({ relative: z.object({ n: z.number(), unit: z.string() }).loose() }).loose(),
]);

const conditionSchema: z.ZodType<Condition> = z.lazy(() =>
  z
    .object({
      and: z.array(conditionSchema).optional(),
      or: z.array(conditionSchema).optional(),
      field: z.string().optional(),
      op: z.string().optional(),
      value: specValueSchema.optional(),
    })
    .loose(),
);

const querySpecSchema: z.ZodType<QuerySpec> = z
  .object({
    version: z.literal(1),
    select: z.array(z.string()).optional(),
    from: z.object({ view: z.string(), metrics: z.array(z.string()), dimensions: z.array(z.string()) }).loose(),
    where: conditionSchema.optional(),
    group_by: z
      .object({
        columns: z.array(z.string()).optional(),
        time_bucket: z.object({ field: z.string(), unit: z.string() }).loose().optional(),
      })
      .loose()
      .optional(),
    order_by: z.array(z.object({ field: z.string(), dir: z.string() }).loose()).optional(),
    limit: z.number().optional(),
  })
  .loose();

/** A spec as stored on a saved report, or null when it is not a v1 spec. */
export function parseQuerySpec(definition: unknown): QuerySpec | null {
  const parsed = querySpecSchema.safeParse(definition);
  return parsed.success ? parsed.data : null;
}

/** A spec as the agent restates it, as JSON text, or null when it is not a v1 spec. */
export function parseQuerySpecJson(text: string): QuerySpec | null {
  try {
    const parsed = querySpecSchema.safeParse(JSON.parse(text));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
